import { phdScannerApiAxios } from "../lib/axios-interceptor";
import { handleResponse } from "../utils/handleResponse";
import { createUrl, transformParams } from "../utils/helpers";
import { extractOpportunityId, isReadableUrl, createReadableOpportunityUrl } from "../utils/opportunityUrlUtils";
import axios from "axios";
import { API } from "../constants";

type PARAMTYPE = Record<string, string | number | boolean>;
export const OpportunityService = {
  async getAllOpportunities(params: PARAMTYPE) {
    const url = createUrl("/api/opportunities", params);
    return handleResponse(() => phdScannerApiAxios.get(url));
  },

   async getRelatedOpportunities(oppId: string) {
    return handleResponse(() =>
      phdScannerApiAxios.get(`api/opportunities/related/${oppId}`),
    );
  },

  async savedUserPreference(reqData: any) {
    return handleResponse(() =>
      phdScannerApiAxios.post("api/user/preferences", reqData),
    );
  },

  async getUserPreference(userId: string) {
    return handleResponse(() =>
      phdScannerApiAxios.get(`api/user/preferences/${userId}`),
    );
  },

  async getOpportunitiesByUserPreference(
    userId: string,
    limit: number = 30,
    offset: number = 0
  ) {
    return handleResponse(() =>
      phdScannerApiAxios.get(`/api/opportunities/user/${userId}`, {
        params: { limit, offset }
      })
    );
  },

 

  async getOpportunityCountries(params: PARAMTYPE) {
    const url = createUrl("/api/countries", params);
    return handleResponse(() => phdScannerApiAxios.get(url));
  },

  async getOpportunityUniversities(params: PARAMTYPE) {
    const url = createUrl("/api/universities", params);
    return handleResponse(() => phdScannerApiAxios.get(url));
  },

  async getOpportunityDiscipline(params: PARAMTYPE) {
    const url = createUrl("/api/disciplines", params);
    return handleResponse(() => phdScannerApiAxios.get(url));
  },
  

  async getOpportunityCountryUni(params: PARAMTYPE) {
    const url = createUrl("/api/opportunities/countries/universities", params);
    return handleResponse(() => phdScannerApiAxios.get(url));
  },
  async getOpportunityCountriesCount() {
    return handleResponse(() =>
      phdScannerApiAxios.get("/api/opportunities/countries/counts"),
    );
  },
  async getOpportunityDisciplineCount() {
    return handleResponse(() =>
      phdScannerApiAxios.get("/api/opportunities/discipline/counts"),
    );
  },

  async getOpportunityUniversitiesCounts() {
    return handleResponse(() =>
      phdScannerApiAxios.get("/api/opportunities/universities/counts"),
    );
  },

  async getOpportunityUniversitiesCountries() {
    return handleResponse(() =>
      phdScannerApiAxios.get("/api/admin/universities/countries"),
    );
  },

  async getCountryOpportunityById(id: string) {
    return handleResponse(() =>
      phdScannerApiAxios.get(`/api/opportunities/countries/${id}`),
    );
  },


  async deleteMessageById(id: string) {
    return handleResponse(() => phdScannerApiAxios.delete(`api/contact?id=${id}`));
  },
  
  async getMessageById() {
    return handleResponse(() => phdScannerApiAxios.get(`/api/admin/contact`));
  },

  async getOpportunityById(identifierInput: string) {
    // Determine if input is a readable URL or direct ID
    const id = isReadableUrl(identifierInput) 
      ? extractOpportunityId(identifierInput)
      : identifierInput;

    return handleResponse(() => phdScannerApiAxios.get(`/api/opportunities/${id}`));
  },

  async createOpportunity(reqData: any) {
    console.log(`reqData data : ${JSON.stringify(reqData)}`);
    return handleResponse(() =>
      phdScannerApiAxios.post("/api/admin/opportunities", reqData),
    );
  },

  // Utility to handle SaaS token expiration
  handleSaasAuthError(error: any) {
    // Check for HTTP status or error message indicating token expiration
    const status = error?.response?.status;
    const message = error?.response?.data?.error || error?.message;
    if (
      status === 401 || status === 403 ||
      (typeof message === 'string' &&
        (message.toLowerCase().includes('token expired') ||
         message.toLowerCase().includes('invalid token') ||
         message.toLowerCase().includes('not authenticated')))
    ) {
      // Clear SaaS auth data and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      
      // Redirect to correct login path based on domain
      const isSaaSSubdomain = window.location.hostname !== 'phdscanner.com' && 
                             window.location.hostname !== 'www.phdscanner.com' &&
                             !window.location.hostname.includes('localhost');
      
      if (isSaaSSubdomain) {
        window.location.href = '/login';
      } else {
        window.location.href = '/saas/login';
      }
      return true;
    }
    return false;
  },

  async createSaasOpportunity(reqData: any) {
    console.log(`reqData data : ${JSON.stringify(reqData)}`);
    
    // Create a separate axios instance for SaaS calls
    const saasAxios = axios.create({
      baseURL: API,
      timeout: 120000,
      withCredentials: true,
    });
    
    // Add interceptor for SaaS authentication
    saasAxios.interceptors.request.use((config) => {
      const headers = config.headers || {};
      
      // Get token from localStorage for SaaS users
      const accessToken = localStorage.getItem("token");
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }
      
      // Add tenant header
      const tenant = localStorage.getItem("tenant");
      if (tenant) {
        headers["X-Tenant"] = tenant;
      }
      
      return config;
    });
    
    return handleResponse(async () => {
      try {
        return await saasAxios.post("/api/saas/opportunities", reqData);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  async getSaasOpportunities(params: PARAMTYPE = {}) {
    // Create a separate axios instance for SaaS calls
    const saasAxios = axios.create({
      baseURL: API,
      timeout: 120000,
      withCredentials: true,
    });
    
    // Add interceptor for SaaS authentication
    saasAxios.interceptors.request.use((config) => {
      const headers = config.headers || {};
      
      // Get token from localStorage for SaaS users
      const accessToken = localStorage.getItem("token");
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }
      
      // Add tenant header
      const tenant = localStorage.getItem("tenant");
      if (tenant) {
        headers["X-Tenant"] = tenant;
      }
      
      return config;
    });
    
    const url = createUrl("/api/saas/opportunities", params);
    return handleResponse(async () => {
      try {
        return await saasAxios.get(url);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  

  async updateOpportunityById({ id, data }: { id: string; data: any }) {
    return handleResponse(() =>
      phdScannerApiAxios.put(`/api/admin/opportunities/${id}`, data) // Use backticks here
    );
  },

  async deleteOpportunityById(id: string) {
    return handleResponse(() =>
      phdScannerApiAxios.delete(`/api/admin/opportunities/${id}`),
    );
  },

  async getSavedOpportunities() {
    return handleResponse(() =>
      phdScannerApiAxios.get("/api/user/opportunity/save"),
    );
  },

  async getSavedOpportunitiesDetails() {
    return handleResponse(() =>
      phdScannerApiAxios.get("/api/user/opportunity/save/details"),
    );
  },

  async createSavedOpportunity(reqData: any) {
    return handleResponse(() =>
        phdScannerApiAxios.post("/api/user/opportunity/save", reqData),
    );
  },
  async deleteSavedOpportunities(reqData: any) {
    return handleResponse(() =>
      phdScannerApiAxios.delete("/api/user/opportunity/save", {
        data: reqData, // Pass the request body here
      })
    );
  },

  
  // Method to generate a link for navigation
  generateOpportunityLink(opportunity: {
    id: string;
    country: string;
    university: string;
    title: string;
  }): string {
    return `phd-vacancies-${createReadableOpportunityUrl(
      opportunity.id, 
      opportunity.country,
      opportunity.university, 
      opportunity.title
    )}`;
  },

  async getCustomerById(customerId: string, token: string) {
    return handleResponse(() =>
      phdScannerApiAxios.get(`/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    );
  },

  async getCurrentCustomer() {
    
    // Create a separate axios instance for SaaS calls
    const saasAxios = axios.create({
      baseURL: API,
      timeout: 120000,
      withCredentials: true,
    });
    
    // Add interceptor for SaaS authentication
    saasAxios.interceptors.request.use((config) => {
      const headers = config.headers || {};
      
      // Get token from localStorage for SaaS users
      const accessToken = localStorage.getItem("token");
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }
      
      // Add tenant header
      const tenant = localStorage.getItem("tenant");
      if (tenant) {
        headers["X-Tenant"] = tenant;
      }
      
      return config;
    });
    
    return handleResponse(async () => {
      try {
        return await saasAxios.get("/api/customer/me");
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  async getSaasOpportunityById(id: string) {
    // Create a separate axios instance for SaaS calls
    const saasAxios = axios.create({
      baseURL: API,
      timeout: 120000,
      withCredentials: true,
    });
    saasAxios.interceptors.request.use((config) => {
      const headers = config.headers || {};
      const accessToken = localStorage.getItem("token");
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      const tenant = localStorage.getItem("tenant");
      if (tenant) headers["X-Tenant"] = tenant;
      return config;
    });
    return handleResponse(async () => {
      try {
        return await saasAxios.get(`/api/saas/opportunities/${id}`);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  async deleteSaasOpportunityById(id: string) {
    const saasAxios = axios.create({
      baseURL: API,
      timeout: 120000,
      withCredentials: true,
    });
    saasAxios.interceptors.request.use((config) => {
      const headers = config.headers || {};
      const accessToken = localStorage.getItem("token");
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      const tenant = localStorage.getItem("tenant");
      if (tenant) headers["X-Tenant"] = tenant;
      return config;
    });
    return handleResponse(async () => {
      try {
        return await saasAxios.delete(`/api/saas/opportunities/${id}`);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  async updateSaasOpportunityById(id: string, data: any) {
    const saasAxios = axios.create({
      baseURL: API,
      timeout: 120000,
      withCredentials: true,
    });
    saasAxios.interceptors.request.use((config) => {
      const headers = config.headers || {};
      const accessToken = localStorage.getItem("token");
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      const tenant = localStorage.getItem("tenant");
      if (tenant) headers["X-Tenant"] = tenant;
      return config;
    });
    return handleResponse(async () => {
      try {
        return await saasAxios.put(`/api/saas/opportunities/${id}`, data);
      } catch (error) {
        if (this.handleSaasAuthError(error)) return {};
        throw error;
      }
    });
  },

  async getMostViewedOpportunities() {
    return handleResponse(() => phdScannerApiAxios.get('/api/opportunities/most-viewed'));
  },
};
